import React from "react";
import { motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import {
  CreditCard, Plus, Trash2, Star, Loader2, AlertCircle, X, Lock, Banknote, ShieldCheck,
} from "lucide-react";

let _stripePromise = null;
function getStripePromise(pk) {
  if (!_stripePromise && pk) _stripePromise = loadStripe(pk);
  return _stripePromise;
}

function formatBrand(brand) {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatSortCode(sc) {
  if (!sc) return "";
  // Stripe returns BACS sort code as e.g. "108800" → render "10-88-00"
  return sc.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3");
}

export default function SavedPaymentMethodsPanel() {
  const [methods, setMethods] = React.useState([]);
  const [defaultId, setDefaultId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [acting, setActing] = React.useState(null);
  const [setupType, setSetupType] = React.useState(null); // "card" | "bacs_debit" | null

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/customer/payment-methods");
      setMethods(data.payment_methods || []);
      setDefaultId(data.default_payment_method_id || null);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const deleteMethod = async (pmId) => {
    if (!window.confirm("Remove this payment method? You'll need to re-enter it for future jobs.")) return;
    setActing(pmId);
    try {
      await apiClient.delete(`/customer/payment-methods/${pmId}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove payment method");
    } finally {
      setActing(null);
    }
  };

  const setDefault = async (pmId) => {
    setActing(pmId);
    try {
      await apiClient.post(`/customer/payment-methods/${pmId}/default`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to set default");
    } finally {
      setActing(null);
    }
  };

  return (
    <section className="mb-16" data-testid="saved-payment-methods-section">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">
            Saved payment methods
          </h2>
          <p className="mt-1 text-sm text-[#475569] max-w-xl">
            Save a card or set up Direct Debit so we can auto-charge the 70% balance on deposit jobs the moment your driver finishes — no extra steps for you.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSetupType("card")}
            data-testid="add-card-button"
            className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2.5 text-sm font-semibold transition-colors"
          >
            <CreditCard size={14} /> Add card
          </button>
          <button
            onClick={() => setSetupType("bacs_debit")}
            data-testid="add-bacs-button"
            className="inline-flex items-center gap-2 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55]/5 px-4 py-2.5 text-sm font-semibold transition-colors"
          >
            <Banknote size={14} /> Add Direct Debit
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="saved-pm-error" className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : methods.length === 0 ? (
        <div data-testid="saved-pm-empty" className="bg-white border border-dashed border-[#0A0A0A]/20 p-8 text-center">
          <CreditCard size={28} className="mx-auto text-[#475569]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">No saved payment methods yet</p>
          <p className="mt-1 text-xs text-[#475569]">Save a card to skip checkout on future jobs.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="saved-pm-list">
          {methods.map((m) => (
            <div key={m.id} data-testid={`saved-pm-${m.id}`} className="bg-white border border-[#0A0A0A]/10 p-4 flex items-center gap-4 flex-wrap">
              <div className="shrink-0 h-10 w-14 bg-[#F5F5F5] border border-[#0A0A0A]/10 flex items-center justify-center">
                {m.type === "card" ? <CreditCard size={18} className="text-[#1E4D55]" /> : <Banknote size={18} className="text-[#1E4D55]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#0A0A0A]">
                    {m.type === "card"
                      ? `${formatBrand(m.brand)} ···· ${m.last4}`
                      : `Direct Debit · ${formatSortCode(m.sort_code)} ···· ${m.last4}`}
                  </span>
                  {m.is_default && (
                    <span data-testid={`saved-pm-default-badge-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[#2c8642] border border-[#5BB247]/40 bg-[#5BB247]/5 px-1.5 py-0.5">
                      <Star size={9} fill="#5BB247" /> Default
                    </span>
                  )}
                </div>
                {m.type === "card" && (
                  <p className="mt-1 text-xs text-[#475569]">Expires {String(m.exp_month).padStart(2, "0")}/{m.exp_year}</p>
                )}
                {m.type === "bacs_debit" && (
                  <p className="mt-1 text-xs text-[#475569]">First payment may take 3-5 business days to clear.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!m.is_default && (
                  <button
                    onClick={() => setDefault(m.id)}
                    disabled={acting === m.id}
                    data-testid={`saved-pm-default-${m.id}`}
                    className="text-xs font-semibold text-[#1E4D55] hover:underline disabled:opacity-60"
                  >
                    Set default
                  </button>
                )}
                <button
                  onClick={() => deleteMethod(m.id)}
                  disabled={acting === m.id}
                  data-testid={`saved-pm-delete-${m.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#B91C1C] hover:underline disabled:opacity-60"
                >
                  {acting === m.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {setupType && (
        <SetupMethodModal
          type={setupType}
          onClose={() => setSetupType(null)}
          onSaved={async () => {
            setSetupType(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function SetupMethodModal({ type, onClose, onSaved }) {
  const [clientSecret, setClientSecret] = React.useState("");
  const [pk, setPk] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post("/customer/payment-methods/setup-intent", { type });
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setPk(data.publishable_key);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't initialise setup");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [type]);

  const title = type === "bacs_debit" ? "Add Direct Debit" : "Add a new card";
  return (
    <div data-testid="setup-method-modal" className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Secure setup</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">{title}</h3>
            {type === "bacs_debit" && (
              <p className="mt-2 text-xs text-[#475569]">
                Authorise Raanah to take Direct Debit payments per the BACS Direct Debit Guarantee. First payment may take 3-5 business days to clear.
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="setup-method-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        {loading && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#475569]"><Loader2 size={14} className="animate-spin" /> Setting up…</div>
        )}

        {error && (
          <div data-testid="setup-method-error" className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && clientSecret && pk && (
          <Elements stripe={getStripePromise(pk)} options={{ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#1E4D55" } } }}>
            <SetupForm onSaved={onSaved} type={type} />
          </Elements>
        )}

        <p className="mt-6 text-[11px] text-[#475569] inline-flex items-center gap-1">
          <Lock size={11} /> Powered by Stripe. {type === "bacs_debit" ? "DD mandates handled per BACS Service User Guidelines." : "Card details never touch Raanah's servers."}
        </p>
      </motion.div>
    </div>
  );
}

function SetupForm({ onSaved, type }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: stripeError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (stripeError) {
      setError(stripeError.message || "Setup failed");
      setSubmitting(false);
      return;
    }
    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="setup-method-form">
      <PaymentElement />
      {error && (
        <div data-testid="setup-method-card-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="setup-method-save-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 font-semibold transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        {submitting ? "Saving…" : (type === "bacs_debit" ? "Authorise Direct Debit" : "Save card")}
      </button>
    </form>
  );
}
